import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { addMonths, endOfMonth, format, isSameMonth, startOfMonth, subMonths, UTCDate } from '@/utils/dates';
import { Currency } from '@seapoint/server/enums';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView, CategoryView } from '../../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { MoneyMovementChart } from '../../components/charts/money-movement-chart';
import { getCashFlowChartData } from '../../utils/get-cash-flow-chart-data';

type CashFlowMoneyMovementProps = {
  accounts: AccountView[];
  categories: CategoryView[];
  baseCurrency: SupportedCountryCurrency;
  referenceDate: Date;
  onReferenceDateChange: (date: Date) => void;
  highlightedMonth: Date;
  onHighlightedMonthChange: (date: Date) => void;
};

export const CashFlowMoneyMovement = ({
  accounts,
  categories,
  baseCurrency,
  referenceDate,
  onReferenceDateChange,
  highlightedMonth,
  onHighlightedMonthChange,
}: CashFlowMoneyMovementProps) => {
  const analytics = useAnalytics();

  // Calculate Money In and Money Out for the highlighted month using account transactions
  // This ensures consistency with the chart which also uses account transactions
  // Financial Items and Internal Transfers are excluded to match reporting table
  // Money In = SUM(Revenue)
  // Money Out = SUM(All Expenses)
  const highlightedMonthData = useMemo(() => {
    const allTransactions = accounts.flatMap((account) => account.transactions || []);
    const monthStart = startOfMonth(highlightedMonth);
    const monthEnd = endOfMonth(highlightedMonth);
    const chartDataForMonth = getCashFlowChartData({
      transactions: allTransactions,
      categories,
      startDate: monthStart,
      endDate: monthEnd,
    });
    // Get the data point for the highlighted month
    return (
      chartDataForMonth.find((point) => isSameMonth(point.date, highlightedMonth)) || {
        moneyIn: 0,
        moneyOut: 0,
      }
    );
  }, [accounts, categories, highlightedMonth]);

  // Prepare Chart Data
  // Calculate data for 6 months ending at referenceDate
  // This allows showing 2 extra months to the left of the table's 4-month view
  // Table shows: Month-3, Month-2, Month-1, Month-0
  // Chart shows: Month-5, Month-4, Month-3, Month-2, Month-1, Month-0
  const chartData = useMemo(() => {
    const chartStartDate = startOfMonth(subMonths(referenceDate, 5));
    const chartEndDate = endOfMonth(referenceDate);
    const allTransactions = accounts.flatMap((account) => account.transactions || []);
    const rawChartData = getCashFlowChartData({
      transactions: allTransactions,
      categories,
      startDate: chartStartDate,
      endDate: chartEndDate,
    });
    // Transform to match MoneyMovementChart expected format
    return rawChartData.map((point) => ({
      period: point.period,
      moneyIn: point.moneyIn,
      moneyOut: point.moneyOut,
      date: point.date.toISOString(),
      rawDate: point.date,
    }));
  }, [accounts, categories, referenceDate]);

  const totalMoneyIn = highlightedMonthData.moneyIn;
  const totalMoneyOut = highlightedMonthData.moneyOut;

  const formattedMoneyIn = getAmountWithCurrency({
    amount: totalMoneyIn,
    currency: baseCurrency,
    isAccountBalance: true,
  });

  const formattedMoneyOut = getAmountWithCurrency({
    amount: totalMoneyOut,
    currency: baseCurrency,
    isAccountBalance: true,
  });

  const trackMonthChange = (direction: 'previous' | 'next', targetMonth: Date) => {
    analytics.track('reporting_month_navigated', {
      direction,
      current_month: format(referenceDate, 'MMM yyyy'),
      target_month: format(targetMonth, 'MMM yyyy'),
    });
  };

  return (
    <Stack space={6} className="w-full px-6 py-4">
      <Inline align="center" className="w-full" justify="between" wrap="wrap" space={4}>
        <Inline space={4} align="center">
          <h2 className="text-base font-medium whitespace-nowrap text-foreground">Money Movement</h2>

          <div className="flex flex-wrap items-center gap-2">
            <Inline space={2} align="center" className="px-3 py-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 p-0 text-muted-foreground hover:bg-transparent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Go to previous month"
                onClick={() => {
                  const newRef = subMonths(referenceDate, 1);
                  onReferenceDateChange(newRef);
                  onHighlightedMonthChange(newRef);

                  trackMonthChange('previous', newRef);
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <span className="text-base font-normal text-foreground">{format(highlightedMonth, 'MMM yyyy')}</span>

              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 p-0 text-muted-foreground hover:bg-transparent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                disabled={isSameMonth(referenceDate, new UTCDate())}
                aria-label="Go to next month"
                onClick={() => {
                  const newRef = addMonths(referenceDate, 1);
                  onReferenceDateChange(newRef);
                  onHighlightedMonthChange(newRef);

                  trackMonthChange('next', newRef);
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Inline>
          </div>
        </Inline>
      </Inline>

      <Inline space={6} align="center">
        <Stack space={1}>
          <p className="text-xs text-muted-foreground">Money In</p>
          <p className="text-xl font-medium text-success">{formattedMoneyIn}</p>
        </Stack>
        <Stack space={1}>
          <p className="text-xs text-muted-foreground">Money Out</p>
          <p className="text-xl font-medium text-foreground">-{formattedMoneyOut}</p>
        </Stack>
      </Inline>

      {/* Chart Alignment Wrapper */}
      <div className="w-full pt-2">
        <div className="flex w-full items-end">
          {/* Left Spacer */}
          <div style={{ width: 'calc(12% - 84px)' }} />

          {/* Chart Area: 6 months (66%) + YAxis (90px) */}
          <div style={{ width: 'calc(66% + 90px)' }}>
            <MoneyMovementChart
              data={chartData}
              baseCurrency={baseCurrency as Currency}
              height={250}
              referenceDate={referenceDate}
              highlightedMonth={highlightedMonth}
              onMonthSelect={onHighlightedMonthChange}
            />
          </div>

          {/* Right Spacer */}
          <div style={{ width: 'calc(22% - 6px)' }} />
        </div>
      </div>
    </Stack>
  );
};
